import { Language } from '../../models/serializable/language.js';
import { VersionRange } from '../../models/core/version-range.js';
import { cloneMap, DataModelBuilder, isSameList,
  isSameMap, mapToData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { PointOfInterestContentType } from '../../models/serializable/point-of-interest-content-type.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class RelatedPointsOfInterestComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'f4f419fb84324d80b2778cc47f41d3af';
  static readonly SCHEMA_VERSION: number = 2;
  static readonly DEFAULT_RENDER_ORDER: number = 10;

  constructor(
    public readonly names: Map<Language, string>,
    public readonly items: string[],
    public readonly contentType: PointOfInterestContentType | null,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId() {
    return RelatedPointsOfInterestComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, RelatedPointsOfInterestComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static fromMap(context: SerializationContext, map: SerializedData): RelatedPointsOfInterestComponent {
    return new RelatedPointsOfInterestComponent(
        MapUtils.getMap(map, 'names', (k, v) => {
          return { key: k as Language, value: v as string };
        }),
        MapUtils.getList<string>(map, 'items', (e) => e as string),
        MapUtils.getOrNull<PointOfInterestContentType | null>(map, 'contentType'),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'names': mapToData(this.names, (k, v) => {
        return { key: k.toString(), value: v };
      }),
      'items': this.items,
      'contentType': this.contentType,
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new RelatedPointsOfInterestComponent(
        cloneMap(this.names),
        this.items,
        this.contentType,
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: RelatedPointsOfInterestComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameMap(other.names, this.names) &&
          isSameList(other.items, this.items) &&
          other.contentType === this.contentType &&
          other.renderOrder === this.renderOrder &&
          other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class RelatedPointsOfInterestComponentBuilder extends DataModelBuilder<RelatedPointsOfInterestComponent> {
  build(context: SerializationContext, map: SerializedData): RelatedPointsOfInterestComponent {
    return RelatedPointsOfInterestComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(RelatedPointsOfInterestComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(RelatedPointsOfInterestComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', RelatedPointsOfInterestComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
